import {matchExpression, Expressions} from '@packages/systems/wfdl';
import {constant, identity} from '@packages/utilities/fp/utils';

import {type ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  GET_FROM_DATA,
  TAG_BY_NODE_TYPE,
} from '@packages/systems/core/constants/TagToTypeMigration';
import {weakMemo} from '@packages/utilities/memo';
import {isStringElement} from '@packages/utilities/elementTypeUtils';
import {getName} from '@packages/systems/wfdl-Qualified';

const NODE_TAG_GETTERS: {
  [key: string]: any;
} = Object.keys(TAG_BY_NODE_TYPE).reduce<Record<string, any>>(
  (result, nodeType) => {
    const tagName = TAG_BY_NODE_TYPE[nodeType];
    result[nodeType] =
      tagName === GET_FROM_DATA ? getTagFromData : () => tagName;
    return result;
  },
  {}
);

const getTextOrEnum = matchExpression({
  Text: identity,
  Enum: identity,
  _: constant('div'),
});

/**
 * Returns the value of `element.data.tag`, defaulting to `'div'` if no value is found.
 *
 * This function is intended for use with elements that have configurable tags,
 * such as `Heading` elements, that store the tag value in element data.
 *
 * *Note*: This function does _not_ represent an exhaustive mapping of element types to
 * HTML tag names and may return `'div'` for elements that do not resolve to a `div` in
 * rendered page content. If a more exhaustive lookup is needed, `getTag` can be used.
 *
 */
export function getTagFromData(element: ExpressionElement): string {
  const tag = Expressions.getIn(Expressions.getElementData(element), [
    {in: 'Record', at: 'tag'},
  ]);

  if (tag != null) return getTextOrEnum(tag);

  return 'div';
}

/**
 * Returns the HTML tag name that the provided element should resolve to,
 * defaulting to `'div'` if there is no tag associated with the element's type.
 *
 * This function is intended for specific use cases when the exact HTML tag name
 * representing the provided element needs to be known, such as when serializing
 * elements to `JSONNode`s or matching elements to tag-based style block selectors.
 *
 * *Note*: `String` element types do not map to an HTML tag and will return an empty string.
 */
export const getTag = weakMemo((element: ExpressionElement): string => {
  if (isStringElement(element)) {
    return '';
  }

  const tagGetter = NODE_TAG_GETTERS[getName(element.type)];

  if (tagGetter) {
    return tagGetter(element);
  }

  return 'div';
});

export const isTagBound = (element: ExpressionElement): boolean => {
  const tag = Expressions.getIn(Expressions.getElementData(element), [
    {in: 'Record', at: 'tag'},
  ]);

  return tag !== undefined && Expressions.isBinding(tag);
};
